import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { PlayerStatTable } from "./PlayerStatTable";
import { SavedFilterTemplatesControlsView } from "../filterTemplates/SavedFilterTemplatesControls";
import { ParametersSelector } from "../ParametersSelector";

export const StickyTableHead = styled.thead`
  position: sticky !important;
  top: 0;
  background-color: ${(props) => props.theme.black};
  z-index: 1;
`;

const ClickableStatsHint = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  color: ${(props) => props.theme.blueish};
  font-family: "SatoshiMedium";
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
`;

const ClickableStatsHintDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${(props) => props.theme.green};
  box-shadow: 0 0 8px ${(props) => props.theme.green};
  flex-shrink: 0;
`;

const PlayerStat = (props) => {
  const {
    statistics,
    season,
    onDataChange,
    mainStat,
    loadingStat,
    tempSelectedFilters,
    selectedMatchIds,
    onChangeParameters,
    savedTemplatesState,
    playerId,
  } = props;

  const { t } = useTranslation();

  const [tableTypeSorting, setTableTypeSorting] = useState({
    type: "default",
    param: "",
  });
  const [openEventFilterModal, setOpenEventFilterModal] = useState(false);

  const isGoalkeeper =
    Array.isArray(mainStat?.player?.position) &&
    mainStat.player.position.some((item) => Number(item?.id) === 10);

  const normalizedMatches = useMemo(() => {
    return (statistics || []).map((match) => ({
      ...match,
      parameters: [...(match?.parameters || [])].sort(
        (a, b) => (a?.parameter_id || 0) - (b?.parameter_id || 0)
      ),
    }));
  }, [statistics]);

  const selectedMatchIdsSet = useMemo(
    () => new Set((selectedMatchIds || []).map((item) => Number(item))),
    [selectedMatchIds]
  );

  const handleSortTable = (param) => {
    const parameterId = param?.parameter_id;

    if (tableTypeSorting.type === "asc" && parameterId === tableTypeSorting.param) {
      setTableTypeSorting({ type: "desc", param: parameterId });
      return;
    }

    if (tableTypeSorting.type === "desc" && parameterId === tableTypeSorting.param) {
      setTableTypeSorting({ type: "default", param: parameterId });
      return;
    }

    setTableTypeSorting({ type: "asc", param: parameterId });
  };

  const sortTable = (a, b) => {
    if (!tableTypeSorting.param || tableTypeSorting.type === "default") {
      return 0;
    }

    const paramA = a.parameters?.find(
      (param) => param.parameter_id === tableTypeSorting.param
    );
    const paramB = b.parameters?.find(
      (param) => param.parameter_id === tableTypeSorting.param
    );

    if (paramA && paramB) {
      if (tableTypeSorting.type === "asc") {
        return (paramA?.value || 0) - (paramB?.value || 0);
      }

      if (tableTypeSorting.type === "desc") {
        return (paramB?.value || 0) - (paramA?.value || 0);
      }
    }

    if (paramA) return -1;
    if (paramB) return 1;

    return 0;
  };

  const tableData = useMemo(() => {
    if (selectedMatchIdsSet.size === 0) {
      return [];
    }

    return normalizedMatches
      .filter((item) => selectedMatchIdsSet.has(Number(item?.id)))
      .sort(sortTable);
  }, [normalizedMatches, selectedMatchIdsSet, tableTypeSorting]);

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", marginBottom: 30 }}>
        <ClickableStatsHint>
          <ClickableStatsHintDot />
          {t("smrtSearch.clickableStatsHint")}
        </ClickableStatsHint>
        <div
          style={{
            display: "flex",
            gap: 10,
            alignSelf: "flex-end",
            marginBottom: 10,
            width: "100%",
            justifyContent: "flex-end",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <button
            className="buttonFilter"
            style={{ minWidth: 160, width: "fit-content", padding: "0 12px" }}
            onClick={() =>
              onDataChange?.(season, tempSelectedFilters?.checkedIds || [], "xls")
            }
          >
            Download in XLS
          </button>
          <button
            className="buttonFilter"
            style={{ minWidth: 160, width: "fit-content", padding: "0 12px" }}
            onClick={() =>
              onDataChange?.(season, tempSelectedFilters?.checkedIds || [], "xml")
            }
          >
            Download in XML
          </button>
          <div style={{ flex: "0 1 360px", minWidth: 280, maxWidth: 420 }}>
            <SavedFilterTemplatesControlsView
              {...savedTemplatesState}
              pageKey="player_stats"
              showCreateButton={false}
              showUpdateButton={false}
              showStatusMessage={false}
              showModals={false}
            />
          </div>
          <button
            className="buttonFilter"
            style={{ minWidth: 150, width: "fit-content", padding: "0 12px" }}
            onClick={() => setOpenEventFilterModal(true)}
          >
            {t("smrtSearch.filterEvents")}
          </button>
        </div>

        <PlayerStatTable
          data={tableData}
          loading={loadingStat}
          handleSortTable={handleSortTable}
          tableTypeSorting={tableTypeSorting}
          playerName={mainStat?.player?.display_name}
          playerId={playerId}
        />
      </div>

      <ParametersSelector
        open={openEventFilterModal}
        onClose={() => setOpenEventFilterModal(false)}
        type={isGoalkeeper ? "gk" : "player"}
        updateData={onChangeParameters}
        savedTemplatesState={savedTemplatesState}
        savedTemplatesPageKey="player_stats"
      />
    </>
  );
};

export default PlayerStat;
